<template>
    <div class="forecast-invoice-wrapper" ref="invoiceTable">
        <CreateInvoiceModal v-if="showingCreateInvoice" @cancel="showingCreateInvoice = false"
            @create="createInvoice" />
        <EditInvoiceModal v-if="showingEditInvoice" :invoice="selectedInvoice" @cancel="showingEditInvoice = false"
            @update="updateInvoice" />
        <div class="forecast-invoice-wrapper-head">
            <div class="forecast-invoice-wrapper-head-title">
                {{ $t("billing.myInvoices") }}
            </div>
            <div class="forecast-invoice-wrapper-head-actions">
                <a-select v-model="filters.type" style="width: 220px" dropdownClassName="custom-select-dropdown">
                    <a-select-option v-for="(option, index) in invoiceOptions" :key="index" :value="option.value">
                        {{ option.label }}
                    </a-select-option>
                </a-select>
                <SupsisButton v-if="isAdmin" @click="showingCreateInvoice = true">{{ $t("billing.createInvoice") }}
                </SupsisButton>
            </div>
        </div>
        <div class="forecast-invoice-wrapper-content">
            <supsis-table @cursor-changed="cursorChanged" :paginationCursor="paginationCursor"
                :columns="getInvoiceTableColumns()" :rows="allInvoices" :bottomPagination="true" :zebra-row="false" />
        </div>
    </div>
</template>
<script>
import { mapState, mapActions, mapMutations, mapGetters } from "vuex";
import {
    createInvoice,
    forceDeleteInvoice,
    getInvoices,
    markInvoiceAsPaid,
    refundInvoice,
    takePayment,
    updateInvoiceById,
} from "../../../../api/billing";
import SupsisTable from "../../../../components/SupsisTable.vue";
import PlanItemButton from "./PlanItemButton.vue";
import MoneyFormatter from "../../../../components/MoneyFormatter.vue";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import CreateInvoiceModal from "../../../../components/billing/CreateInvoiceModal.vue";
import EditInvoiceModal from "../../../../components/billing/EditInvoiceModal.vue";
import _ from "lodash";

export default {
    name: "ForecastInvoice",
    components: {
        CreateInvoiceModal,
        EditInvoiceModal,
        SupsisButton,
        SupsisTable,
        PlanItemButton,
        MoneyFormatter,
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
            currentPlans: (state) => state.site.currentPlans,
            plans: (state) => state.site.plans,
            isAdmin: (state) => state.auth.adminUser != null,
        }),
        ...mapGetters({
            isSavedCreditCard: "site/isSavedCreditCard",
        }),
        allInvoices() {
            const nextMonth = this.$moment().startOf("month");
            return [
                this.totalForecastPrice &&
                ["monthly", "unpaid", "all"].includes(this.filters.type) && {
                    _id: (nextMonth.valueOf() / 1000).toString(16).slice(0, 8),
                    invalidPayment: false,
                    iyzicoId: null,
                    price: this.totalForecastPrice,
                    createdDate: nextMonth,
                    pdf: null,
                    type: "forecast",
                },
                ...this.invoices,
            ].filter(Boolean);
        },
    },
    data() {
        return {
            showingCreateInvoice: false,
            showingEditInvoice: false,
            selectedInvoice: null,
            totalForecastPrice: null,
            invoices: [],
            filters: {
                type: "all",
            },
            paginationCursor: {
                page: 1,
                perPage: 10,
                total: 0,
            },
            invoiceOptions: [],
            invoiceFocus: this.$route.query.focus === "invoice",
            // API çağrıları için loading state'leri
            refundInvoiceLoading: new Set(), // Birden fazla fatura için ayrı ayrı takip
            getPaymentLoading: new Set(), // Birden fazla fatura için ayrı ayrı takip
        }
    },
    methods: {
        ...mapMutations({
            CHANGE_SUSPEND_STATUS: "site/CHANGE_SUSPEND_STATUS",
        }),
        ...mapActions({
            getRunningInvoice: "site/getRunningInvoice",
        }),
        focusInvoice() {
            this.$nextTick(() => {
                const invoiceElement = this.$refs.invoiceTable;
                if (invoiceElement) {
                    invoiceElement.scrollIntoView({ behavior: "smooth", block: "center" });
                    setTimeout(() => invoiceElement.classList.add("focused"), 0);
                    this.invoiceFocus = false;
                    this.$router.replace({ query: { ...this.$route.query, focus: undefined } });
                }
            });
        },
        copyToClipboard(text) {
            navigator.clipboard
                .writeText(text)
                .then(() => {
                    this.$notification.success({
                        message: "Kopyalandı",
                        description: "İçerik panoya kopyalandı.",
                        duration: 2,
                    });
                })
                .catch(() => {
                    this.$notification.error({
                        message: "Hata",
                        description: "Kopyalama işlemi başarısız oldu.",
                        duration: 2,
                    });
                });
        },
        cursorChanged({ page, perPage }) {
            this.paginationCursor.page = page;
            this.paginationCursor.perPage = perPage;
            this.fetchInvoices();
        },
        fetchInvoices: _.debounce(function (type, changeState = true) {
            const isShowingForecast = ["monthly", "unpaid", "all"].includes(this.filters.type);
            return getInvoices(
                { ...this.filters, type: type || this.filters.type },
                {
                    page: this.paginationCursor.page,
                    perPage: isShowingForecast ? this.paginationCursor.perPage - 1 : this.paginationCursor.perPage,
                }
            ).then((response) => {
                if (changeState) {
                    this.invoices = response.data.invoices;
                    this.paginationCursor.total = response.data.total;
                }
                return response.data;
            });
        }, 400),
        getInvoiceTableColumns() {
            return [
                this.isAdmin && {
                    title: this.$t("billing.paymentId"),
                    dataIndex: "iyzicoId",
                    key: "iyzicoId",
                    width: "120px",
                    renderBodyCell: ({ row }) => {
                        const text = row.paymentTransactionId || "n/A";
                        return (
                            <div class="copyable-cell" onClick={() => this.copyToClipboard(text)}>
                                {text}
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("billing.billType"),
                    field: "type",
                    key: "type",
                    width: "150px",
                    renderBodyCell: ({ row }) => {
                        return <a-tag color="#1C1B1F"> {this.formatBillType(row.type)}</a-tag>;
                    },
                },
                {
                    title: this.$t("billing.invoiceId"),
                    field: "_id",
                    key: "_id",
                    width: "120px",
                    renderBodyCell: ({ row }) => {
                        const text = row._id.slice(0, 8);
                        return (
                            <div class="copyable-cell" onClick={() => this.copyToClipboard(text)}>
                                {text}
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("billing.invoiceAmount"),
                    field: "price",
                    key: "price",
                    width: "150px",
                    renderBodyCell: ({ row }) => {
                        const text = row.type === "forecast" ? `${row.price} TL (Tahmini)` : `${row.price} TL`;
                        return (
                            <div class="copyable-cell" onClick={() => this.copyToClipboard(text)}>
                                {row.type === "forecast" ? (
                                    <span>
                                        <MoneyFormatter price={row.price} /> (Tahmini)
                                    </span>
                                ) : (
                                    <MoneyFormatter price={row.price} />
                                )}
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("billing.invoiceDate"),
                    field: "createdDate",
                    key: "createdDate",
                    width: "150px",
                    renderBodyCell: ({ row }) => {
                        const text = this.$moment(row.createdDate).format("YYYY/MM/DD");
                        return (
                            <div class="copyable-cell" onClick={() => this.copyToClipboard(text)}>
                                {text}
                            </div>
                        );
                    },
                },
                {
                    title: "Son Ödeme Tarihi",
                    field: "dueDate",
                    key: "dueDate",
                    width: "150px",
                    renderBodyCell: ({ row }) => {
                        const text = row.dueDate ? this.$moment(row.dueDate).format("YYYY/MM/DD") : "-";
                        return (
                            <div class="copyable-cell" onClick={() => this.copyToClipboard(text)}>
                                {text}
                            </div>
                        );
                    },
                },
                {
                    title: "Ödeme Tarihi",
                    field: "paymentDate",
                    key: "paymentDate",
                    width: "150px",
                    renderBodyCell: ({ row }) => {
                        const text = row.paymentDate ? this.$moment(row.paymentDate).format("YYYY/MM/DD") : "-";
                        return (
                            <div class="copyable-cell" onClick={() => this.copyToClipboard(text)}>
                                {text}
                            </div>
                        );
                    },
                },
                this.isAdmin && {
                    title: this.$t("billing.invalidBill"),
                    field: "invalidPayment",
                    key: "invalidPayment",
                    width: "120px",
                    renderBodyCell: ({ row }) => {
                        return row.invalidPayment ? (
                            <a-tag color="red">{this.$t("message.yes")}</a-tag>
                        ) : (
                            <a-tag color="green">{this.$t("message.no")}</a-tag>
                        );
                    },
                },
                {
                    title: this.$t("billing.invoiceStatus"),
                    field: "paid",
                    key: "paid",
                    width: "130px",
                    renderBodyCell: ({ row }) => {
                        const PaymentIcon = () => {
                            return (
                                <div
                                    class="payment-icon"
                                    status={
                                        row.refund
                                            ? "refund"
                                            : row.paid
                                                ? "paid"
                                                : row.type === "forecast"
                                                    ? "forecast"
                                                    : "not-paid"
                                    }
                                ></div>
                            );
                        };
                        return (
                            <div class="payment-status">
                                <PaymentIcon />
                                {row.refund
                                    ? this.$t("message.refunded")
                                    : row.paid
                                        ? this.$t("message.paid")
                                        : row.type === "forecast"
                                            ? this.$t("message.pending")
                                            : this.$t("message.unpaid")}
                            </div>
                        );
                    },
                },
                {
                    title: "",
                    field: "pdf",
                    key: "pdf",
                    align: "right",
                    width: "auto",
                    fixed: "right",
                    renderBodyCell: ({ row }) => {
                        const downloadPdf = async () => {
                            const pdfUrl = "https://static.supsis.live/" + row.pdf;
                            window.open(pdfUrl, "_blank");
                        };
                        const showInvoice = () => {
                            if (row.type === "forecast") {
                                this.$router.push(`/billing/invoice/forecast`);
                                return;
                            }
                            this.$router.push(`/billing/invoice/${row._id}`);
                        };

                        const AdminActions = () => {
                            const DeleteInvoice = () => {
                                return (
                                    <PlanItemButton on-click={() => this.deleteInvoice(row._id)}>
                                        Faturayı Sil
                                    </PlanItemButton>
                                );
                            };

                            const RefundInvoice = () => {
                                const isLoading = this.refundInvoiceLoading.has(row._id);
                                return (
                                    !row.refund && (
                                        <PlanItemButton 
                                            on-click={() => this.refundInvoice(row._id)}
                                            disabled={isLoading}
                                            loading={isLoading}
                                        >
                                            Geri Ödeme Yap
                                        </PlanItemButton>
                                    )
                                );
                            };

                            const MarkAsPaid = () => {
                                return (
                                    !row.paid && (
                                        <PlanItemButton on-click={() => this.markAsPaid(row._id)}>
                                            Ödendi İşaretle
                                        </PlanItemButton>
                                    )
                                );
                            };

                            const EditInvoice = () => {
                                return (
                                    row.type !== "forecast" && (
                                        <PlanItemButton on-click={() => this.editInvoice(row)}>
                                            Faturayı Düzenle
                                        </PlanItemButton>
                                    )
                                );
                            };

                            return (
                                row.type !== "forecast" && (
                                    <div style="display: flex; align-items: center; gap: 5px;">
                                        <MarkAsPaid />
                                        {row.paid && row.paymentTransactionId && <RefundInvoice />}
                                        <DeleteInvoice />
                                        <EditInvoice />
                                    </div>
                                )
                            );
                        };

                        return (
                            <div style="display: flex; align-items: center; gap: 5px; justify-content: flex-end;">
                                {row.pdf && (
                                    <PlanItemButton on-click={downloadPdf}>
                                        {this.$t("message.invoicePdf")}
                                    </PlanItemButton>
                                )}
                                <PlanItemButton on-click={showInvoice}>{this.$t("message.viewInvoice")}</PlanItemButton>
                                {!row.paid && row.type !== "forecast" && (
                                    <PlanItemButton 
                                        on-click={() => this.getPayment(row._id)}
                                        disabled={this.getPaymentLoading.has(row._id)}
                                        loading={this.getPaymentLoading.has(row._id)}
                                    >
                                        {this.$t("message.makePayment")}
                                    </PlanItemButton>
                                )}
                                {this.isAdmin && <AdminActions />}
                            </div>
                        );
                    },
                },
            ].filter(Boolean);
        },
        formatBillType(type) {
            const types = {
                none: "None",
                manuel: "Manuel",
                monthly: this.$t("billing.monthly"),
                "monthly-3": "3 Aylık",
                "monthly-6": "6 Aylık",
                annual: "Yıllık",
                "annual-2": "2 Yıllık",
                education: "Eğitim",
                consultancy: "Danışmanlık",
                installation: "Kurulum",
                other: "Diğer",
                "quick-purchase": this.$t("message.packagePurchase"),
                "wallet-deposit": this.$t("billing.balanceTopUp"),
                forecast: this.$t("message.upcomingInvoice"),
            };
            return types[type] || type;
        },
        /**
         * Fatura ödemesi yapar
         * API çağrısı tamamlanana kadar buton disabled kalır
         * @param {string} invoiceId - Ödeme yapılacak fatura ID'si
         */
        async getPayment(invoiceId) {
            // Eğer zaten bu fatura için işlem devam ediyorsa, tekrar tıklamayı engelle
            if (this.getPaymentLoading.has(invoiceId)) {
                return;
            }

            if (!this.isSavedCreditCard) {
                this.$notification.error({
                    message: "Ödeme Yapılamadı",
                    description: "Ödeme yapmak için önce kredi kartınızı kaydediniz.",
                });
                return;
            }

            // Loading state'ini aktif et
            this.getPaymentLoading.add(invoiceId);

            try {
                const response = await takePayment(invoiceId)
                    .then((res) => res.data)
                    .catch((e) => {
                        return { status: "error", message: null };
                    });
                
                if (response.status === "error") {
                    this.$notification.error({
                        message: this.$t("message.paymentFailed"),
                        description: response.message || this.$t("message.paymentProcessError"),
                    });
                    return;
                }

                const isSuspended = this.site.suspend;
                if (isSuspended) {
                    const unpaidInvoices = await this.fetchInvoices("unpaid", false).then((data) => data.invoices);
                    const passedDueDateInvoices = unpaidInvoices.filter((invoice) => {
                        const dueDate = this.$moment(invoice.dueDate);
                        return dueDate.isBefore(this.$moment());
                    });
                    if (passedDueDateInvoices.length <= 0) {
                        this.filters.type = "all";
                        this.CHANGE_SUSPEND_STATUS(false);
                    }
                }

                // Faturaları yeniden çek ve fatura statusünün güncellenmesini bekle
                await this.fetchInvoices();

                this.$notification.success({
                    message: this.$t("message.paymentSuccessful"),
                    description: this.$t("message.paymentProcessSuccessful"),
                });
            } finally {
                // API çağrısı tamamlandığında (başarılı veya hatalı) loading state'ini kaldır
                this.getPaymentLoading.delete(invoiceId);
            }
        },
        async deleteInvoice(invoiceId) {
            await this.$dialog.confirm(this.$t("message.deleteConfirm", { object: this.$t("billing.invoice") }), {
                okText: this.$t("message.continue"),
                cancelText: this.$t("message.cancel"),
            });
            await forceDeleteInvoice(this.site._id, invoiceId);
            this.fetchInvoices();
        },
        /**
         * Fatura geri ödemesi yapar
         * API çağrısı tamamlanana kadar buton disabled kalır
         * @param {string} invoiceId - Geri ödeme yapılacak fatura ID'si
         */
        async refundInvoice(invoiceId) {
            // Eğer zaten bu fatura için işlem devam ediyorsa, tekrar tıklamayı engelle
            if (this.refundInvoiceLoading.has(invoiceId)) {
                return;
            }

            try {
                await this.$dialog.confirm(this.$t("billing.areYouSoreRefundInvoice"), {
                    okText: "Devam Et",
                    cancelText: "İptal",
                });
            } catch {
                // Kullanıcı iptal ettiyse işlemi durdur
                return;
            }

            // Loading state'ini aktif et
            this.refundInvoiceLoading.add(invoiceId);

            try {
                await refundInvoice(invoiceId);
                this.$notification.success({
                    message: this.$t("message.invoiceRefund"),
                    description: this.$t("message.invoiceRefundedSuccessfully"),
                });
                await this.fetchInvoices();
            } finally {
                // API çağrısı tamamlandığında (başarılı veya hatalı) loading state'ini kaldır
                this.refundInvoiceLoading.delete(invoiceId);
            }
        },
        async markAsPaid(invoiceId) {
            await this.$dialog.confirm(this.$t("billing.areYouSurePaid"), {
                okText: this.$t("message.continue"),
                cancelText: this.$t("message.cancel"),
            });
            const isSuccess = await markInvoiceAsPaid(this.site._id, invoiceId)
                .then((res) => res.data)
                .catch(() => false);
            if (!isSuccess) {
                this.$notification.error({
                    message: this.$t("message.invoicePaymentError"),
                    description: this.$t("message.invoicePaymentProcessError"),
                });
                return;
            }
            this.$notification.success({
                message: this.$t("message.invoicePaid"),
                description: this.$t("message.invoiceMarkedAsPaid"),
            });
            this.fetchInvoices();
        },
        editInvoice(invoice) {
            this.selectedInvoice = invoice;
            this.showingEditInvoice = true;
        },
        async updateInvoice(invoiceId, updateData) {
            const success = await updateInvoiceById(invoiceId, updateData)
                .then(() => true)
                .catch(() => false);

            if (!success) {
                this.$notification.error({
                    message: this.$t("message.invoiceUpdateError"),
                    description: this.$t("message.invoiceUpdateFailedDescription"),
                });
                return;
            }

            this.$notification.success({
                message: this.$t("message.invoiceUpdated"),
                description: this.$t("message.invoiceUpdatedSuccessfully"),
            });

            this.showingEditInvoice = false;
            this.fetchInvoices();
        },
        async createInvoice(price, description, createdDate, dueDate, paid, type, paymentDate) {
            const invoice = createInvoice(price, description, createdDate, {
                dueDate,
                paid,
                type,
                paymentDate,
            })
                .then((res) => res.data)
                .catch(() => null);
            if (!invoice) {
                this.$toasted.error(this.$t("billing.createdNewInvoiceFailed"), {
                    duration: 3000,
                    position: "top-center",
                });
                return;
            }
            this.$toasted.success(this.$t("billing.createdNewInvoice"), {
                duration: 3000,
                position: "top-center",
            });
            this.showingCreateInvoice = false;
            this.fetchInvoices();
        },
    },
    mounted() {
        if (this.invoiceFocus) {
            this.focusInvoice();
        }
        const isSuspend = this.site.suspend;
        if (isSuspend) this.filters.type = "unpaid";
        this.getRunningInvoice(true).then((runningInvoice) => {
            this.totalForecastPrice = runningInvoice.endOfMonthForecastPrice;
        });
        this.invoiceOptions = [
            { value: "all", label: "billing.allInvoices" },
            { value: "manual", label: "billing.manualInvoices" },
            { value: "monthly", label: "billing.endOfPeriodInvoices" },
            { value: "purchases", label: "billing.purchaseInvoices" },
            { value: "wallet-deposit", label: "billing.walletDeposit" },
            { value: "unpaid", label: "billing.unpaidInvoices" },
            { value: "paid", label: "billing.paidInvoices" },
        ].map((option) => ({
            value: option.value,
            label: this.$t(option.label),
        }));
    },
    watch: {
        filters: {
            deep: true,
            handler() {
                this.fetchInvoices();
            },
        },
    },
};
</script>
<style lang="scss" scoped>
@keyframes focus-border {
    0% {
        border: 2px solid transparent;
        border-radius: 8px;
    }
    50% {
        border: 2px solid #0288d1;
        border-radius: 8px;
    }
    100% {
        border: 2px solid transparent;
        border-radius: 8px;
    }
}

.forecast-invoice-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    padding: 20px;

    &.focused {
        animation: focus-border 1s ease-in-out 1s;
    }

    .copyable-cell {
        cursor: pointer;
        padding: 4px 8px;
        border-radius: 4px;
        transition: all 0.2s ease;

        &:hover {
            background-color: rgba(2, 136, 209, 0.1);
        }

        &:active {
            background-color: rgba(2, 136, 209, 0.2);
        }
    }

    &-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;

        &-title {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 5px;
            font-size: 20px;
            font-weight: 600;
            color: #1c1b1f;

            svg {
                width: 20px;
                height: 20px;
            }
        }
    }

    &-content {
        width: 100%;
        background: #f1f4f7;
        border-radius: 8px;
        box-sizing: border-box;
        padding: 10px;

        .supsis-table {
            width: 100%;
        }

        .payment-status {
            display: flex;
            align-items: center;
            gap: 5px;

            .payment-icon {
                width: 10px;
                height: 10px;
                border-radius: 100px;

                &[status="paid"] {
                    background-color: #2ecc71;
                }

                &[status="refund"] {
                    background-color: #e74c3c;
                }

                &[status="forecast"] {
                    background-color: #f1c40f;
                }

                &[status="not-paid"] {
                    background-color: #e74c3c;
                }
            }
        }
    }
}
</style>
